import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import FormModal from '../../Modals/FormModal';
import InputField from '../../Forms/Input/InputField';
import SearchInputField from '../../Forms/Input/SearchInputField';
import SelectField from '../../Forms/Input/SelectField';
import TableCheckbox from '../../Forms/Input/TableCheckbox';
import BasicPagination from '../../DataTable/BasicPagination';
import ActionButton from '../../Buttons/ActionButton';
import ProductService from '../../../services/ProductService';
import { useProductCategories } from '../../../hooks/useProductCategories';
import { useProductSubCategories } from '../../../hooks/product/useProductSubCategories';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

interface CatalogProduct {
  id: number;
  code: string;
  name: string;
}

interface SelectedProduct {
  id: number;
  code: string;
  name: string;
}

interface PageMeta {
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const EMPTY_PAGE: PageMeta = { number: 0, size: 10, totalElements: 0, totalPages: 0 };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; productIds: number[] }) => void;
  initial?: { name: string; products: { productId: number; productCode: string; productName: string }[] } | null;
}

const ProductGroupFormModal: React.FC<Props> = ({ isOpen, onClose, onSave, initial }) => {
  const { fetchProductCategoriesDropdown, productCategoriesDropdown } = useProductCategories();
  const { subCategoriesDropdown, loadDropdown } = useProductSubCategories();

  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [subCategoryId, setSubCategoryId] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [items, setItems] = useState<CatalogProduct[]>([]);
  const [metadata, setMetadata] = useState<PageMeta>(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SelectedProduct[]>([]);

  const debouncedSearch = useDebouncedValue(search, 350);

  useEffect(() => {
    if (!isOpen) return;
    fetchProductCategoriesDropdown();
    setSearch('');
    setCategoryId(0);
    setSubCategoryId(0);
    setPage(0);
    if (initial) {
      setName(initial.name);
      setSelected(
        initial.products.map((p) => ({ id: p.productId, code: p.productCode, name: p.productName }))
      );
    } else {
      setName('');
      setSelected([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initial]);

  useEffect(() => {
    if (categoryId) {
      loadDropdown(categoryId);
    }
    setSubCategoryId(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    ProductService.searchCatalog(
      {
        search: debouncedSearch.trim() || undefined,
        categoryId: categoryId || undefined,
        subCategoryId: subCategoryId || undefined,
      },
      page,
      size
    )
      .then((data) => {
        if (!active) return;
        setItems(data?.content ?? []);
        setMetadata({
          number: data?.number ?? 0,
          size: data?.size ?? size,
          totalElements: data?.totalElements ?? 0,
          totalPages: data?.totalPages ?? 0,
        });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen, debouncedSearch, categoryId, subCategoryId, page, size]);

  const selectedIds = useMemo(() => new Set(selected.map((p) => p.id)), [selected]);

  const toggleProduct = (product: CatalogProduct) => {
    setSelected((prev) =>
      selectedIds.has(product.id)
        ? prev.filter((p) => p.id !== product.id)
        : [...prev, { id: product.id, code: product.code, name: product.name }]
    );
  };

  const handleRemove = (id: number) => {
    setSelected((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selected.length === 0) return;
    onSave({ name: name.trim(), productIds: selected.map((p) => p.id) });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={initial ? 'Editar Grupo de Productos' : 'Nuevo Grupo de Productos'}
      maxWidth="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <InputField
          label="Nombre del grupo"
          name="name"
          type="text"
          placeholder="Ej. Cables, Cemento..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          iconKey="edit"
          required
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SearchInputField
            label="Buscar"
            name="productSearch"
            value={search}
            placeholder="Código o nombre..."
            iconKey="search"
            withMargin={false}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <SelectField
            label="Categoría"
            name="categoryId"
            value={categoryId}
            options={[
              { value: 0, label: 'Todas las categorías' },
              ...productCategoriesDropdown.map((c: any) => ({ value: c.id, label: c.name })),
            ]}
            onChange={(e) => {
              setCategoryId(Number(e.target.value));
              setPage(0);
            }}
          />
          <SelectField
            label="Subcategoría"
            name="subCategoryId"
            value={subCategoryId}
            disabled={!categoryId}
            options={[
              { value: 0, label: 'Todas las subcategorías' },
              ...subCategoriesDropdown.map((s: any) => ({ value: s.id, label: s.name })),
            ]}
            onChange={(e) => {
              setSubCategoryId(Number(e.target.value));
              setPage(0);
            }}
          />
        </div>

        <div className="rounded-md border border-stroke dark:border-strokedark">
          {loading ? (
            <div className="py-10 text-center text-sm text-body">Buscando productos...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-body">
              No se encontraron productos.
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full table-auto">
                <thead className="sticky top-0 bg-gray-2 dark:bg-meta-4">
                  <tr className="text-left">
                    <th className="w-10 px-4 py-2"></th>
                    <th className="px-2 py-2 font-medium">Producto</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr
                      key={p.id}
                      className="cursor-pointer border-t border-stroke hover:bg-gray-1 dark:border-strokedark dark:hover:bg-meta-4"
                      onClick={() => toggleProduct(p)}
                    >
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <TableCheckbox checked={selectedIds.has(p.id)} onChange={() => toggleProduct(p)} />
                      </td>
                      <td className="px-2 py-2">{p.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 px-4 pb-4">
            <BasicPagination
              currentPage={metadata.number}
              totalPages={metadata.totalPages}
              totalElements={metadata.totalElements}
              size={metadata.size}
              first={metadata.number === 0}
              last={metadata.number >= metadata.totalPages - 1}
              onPageChange={setPage}
              onSizeChange={(nextSize) => {
                setSize(nextSize);
                setPage(0);
              }}
            />
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold text-black dark:text-white">
            Productos seleccionados ({selected.length})
          </h4>

          {selected.length === 0 ? (
            <p className="text-sm text-body">Aún no has agregado productos.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded-md border border-stroke dark:border-strokedark">
              <table className="w-full table-auto">
                <tbody>
                  {selected.map((p) => (
                    <tr key={p.id} className="border-t border-stroke first:border-t-0 dark:border-strokedark">
                      <td className="px-2 py-2 text-sm">{p.name}</td>
                      <td className="w-10 px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemove(p.id)}
                          className="text-body hover:text-danger"
                          title="Quitar"
                        >
                          <X size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ActionButton
          label="Guardar Grupo"
          type="submit"
          disabled={!name.trim() || selected.length === 0}
          fullWidth
        />
      </form>
    </FormModal>
  );
};

export default ProductGroupFormModal;
